import { createAsyncThunk } from '@reduxjs/toolkit'

import { BLEND_MODES, DEFAULT_BACKGROUNDS, LYRIC_TEXT_COLORS } from '../constants/constants'
import { AppDispatch, RootState } from '../reducers'
import settingsSlice from '../reducers/settingsSlice'
import sessionSlice from '../reducers/sessionSlice'
import { selectImagePath } from '../selectors/current-play-selectors'
import {
  selectCurrentBackgroundBlendMode,
  selectCurrentBackgroundIndex,
  selectCurrentLyricsColorIndex,
  selectPrefs,
} from '../selectors/session-selectors'
import player from '../services/TrackMixer'
import scheduleSectionEvents from './section-animation'

const toggleShowSections = createAsyncThunk<
  void,
  boolean,
  { state: RootState; dispatch: AppDispatch }
>('settingsAction/toggleShowSections', (isShow: boolean, { dispatch, getState }) => {
  player.defaultGamer.toggleSections(isShow)
  dispatch(settingsSlice.actions.setIsShowSections({ isShowSections: isShow }))
})

const toggleShowSectionAnimation = createAsyncThunk<
  void,
  boolean,
  { state: RootState; dispatch: AppDispatch }
>('settingsAction/toggleShowSectionAnimation', (isShowSectionAnimation, { dispatch, getState }) => {
  dispatch(settingsSlice.actions.setShowSectionAnimation({ isShowSectionAnimation }))
  dispatch(scheduleSectionEvents(player.defaultGamer))
})

const toggleVizSwitchingEnabled = createAsyncThunk<
  void,
  { playerIndex: number; isEnabled: boolean },
  { state: RootState; dispatch: AppDispatch }
>(
  'settingsAction/toggleVizSwitchingEnabled',
  ({ playerIndex, isEnabled }, { dispatch, getState }) => {
    dispatch(settingsSlice.actions.setIsVizSwitchingEnabled({ playerIndex, isEnabled })) // TODO
    const gamer = player._gamers[playerIndex].gamer
    dispatch(scheduleSectionEvents(gamer))
  }
)

const toggleNoviceAssistEnabled = createAsyncThunk<
  void,
  boolean,
  { state: RootState; dispatch: AppDispatch }
>(
  'settingsAction/toggleNoviceAssistEnabled',
  (isNoviceAssistEnabled: boolean, { dispatch, getState }) => {
    player.defaultGamer.toggleNoviceAssist(isNoviceAssistEnabled)
    dispatch(settingsSlice.actions.setIsNoviceAssistEnabled({ isNoviceAssistEnabled }))
  }
)

const cycleLyricsTextColor = createAsyncThunk<
  void,
  void,
  { state: RootState; dispatch: AppDispatch }
>('settingsAction/changeTheme', (useThemeIndex, { dispatch, getState }) => {
  const currentColorIndex = selectCurrentLyricsColorIndex(getState())
  const nextColorIndex = (currentColorIndex + 1) % LYRIC_TEXT_COLORS.length
  dispatch(sessionSlice.actions.setLyricsColorIndex(nextColorIndex))
})

const nextBackgroundImage = createAsyncThunk<void, number, { state: RootState }>(
  'session/nextBackgroundImage',
  (incrementBy, { dispatch, getState }) => {
    const state = getState()
    const { background } = selectPrefs(state)
    const currentBackgroundIndex = selectCurrentBackgroundIndex(state)
    const remoteImagePath = selectImagePath(state)
    if (incrementBy === 0) {
      if (background === 'track') {
        dispatch(sessionSlice.actions.setCurrentBackgroundIndex(DEFAULT_BACKGROUNDS.length))
        // TODO: restore filter and text color for current track!
      } else {
        if (background.startsWith('#')) {
          dispatch(sessionSlice.actions.setCurrentBackgroundIndex(-1))
        } else {
          const presetIndex = DEFAULT_BACKGROUNDS.indexOf(background)
          if (presetIndex >= 0) {
            dispatch(sessionSlice.actions.setCurrentBackgroundIndex(presetIndex))
          }
        }
        dispatch(sessionSlice.actions.setCurrentBackgroundBlendMode(0))
      }
      return
    }
    const lastBackgroundIndex = DEFAULT_BACKGROUNDS.length + (remoteImagePath ? 1 : 0) - 1
    const nextIndex = currentBackgroundIndex < lastBackgroundIndex ? currentBackgroundIndex + 1 : -1
    dispatch(sessionSlice.actions.setCurrentBackgroundIndex(nextIndex))
  }
)

const nextBackgroundEffect = createAsyncThunk<void, void, { state: RootState }>(
  'session/nextBackgroundEffect',
  (_, { dispatch, getState }) => {
    const state = getState()
    const currentIndex = selectCurrentBackgroundBlendMode(state)
    const nextIndex = (currentIndex + 1) % BLEND_MODES.length
    dispatch(sessionSlice.actions.setCurrentBackgroundBlendMode(nextIndex))
  }
)

export {
  cycleLyricsTextColor,
  nextBackgroundImage,
  nextBackgroundEffect,
  toggleShowSections,
  toggleNoviceAssistEnabled,
  toggleVizSwitchingEnabled,
  toggleShowSectionAnimation,
}
